import { Button, Stack } from '@mui/material'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import { VailAccordion, VailSelect, VailTextField } from '@/components'
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import WorkshopTitlePreview from '@/screens/ComponentLibrary/components/WorkshopTitlePreview'
import {
  useCreateCustomComponent,
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { canUseName, safeCypressID } from '@/utils'
import { ComponentType, CreateCustomComponentInput } from '@/utils/api'

export type SettingsTabProps = {
  type: ComponentType
  label: string
}

export default function SettingsTab({ type, label }: SettingsTabProps) {
  if (!type || !label)
    throw new Error('type and label are required props for SettingsTab')
  const form = useForm<CreateCustomComponentInput>({
    defaultValues: { type },
  })

  const navigate = useNavigate()
  const { orgID } = useParams()
  const { componentID, useUrl } = useComponentProvider()
  const createComponent = useCreateCustomComponent(orgID)
  const currentComponent = useGetCustomComponent(componentID)
  const updateComponent = useUpdateCustomComponent(componentID)
  const { reset } = form

  useEffect(() => {
    if (!currentComponent.data) return
    const { name, description } = currentComponent.data
    reset({ description, name })
  }, [currentComponent.data, reset])

  const onSubmit = form.handleSubmit(async (data) => {
    if (componentID)
      return await updateComponent.mutateAsync({
        ...data,
        id: componentID,
        type,
      })
    const res = await createComponent.mutateAsync({
      ...data,
      type,
    })
    if (useUrl) navigate(`../${res?.id}`)
  })

  return (
    <FormProvider {...form}>
      <Stack
        component="form"
        height="100%"
        justifyContent="start"
        onSubmit={onSubmit}
      >
        <Button
          id={safeCypressID('save config')}
          sx={{ alignSelf: 'flex-end', mb: 2 }}
          type="submit"
          variant="contained"
        >
          Save
        </Button>
        <VailAccordion
          defaultExpanded
          useDefaultBox
          detailsSx={{
            display: 'flex',
            flexDirection: ['column', null, 'row'],
            gap: 2,
          }}
          title="Description"
        >
          <Stack flex={1} gap={2}>
            <VailTextField
              control={form.control}
              label="Name"
              name="name"
              rules={{
                validate: {
                  canUseName: async (data) =>
                    canUseName(orgID, componentID, data),
                },
              }}
            />
            <WorkshopTitlePreview name={form.watch('name')} />
            <VailSelect
              disabled
              control={form.control}
              defaultValue={type}
              label="Type"
              name="type"
              options={[{ label, value: type }]}
            />
          </Stack>
          <Stack flex={1}>
            <VailTextField
              multiline
              control={form.control}
              label="Description"
              maxRows={5}
              minRows={5}
              name="description"
              rules={{ required: false }}
            />
          </Stack>
        </VailAccordion>
      </Stack>
    </FormProvider>
  )
}
